import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import PreviewTable from "./PreviewTable";
import { API, MappingEditor, PhaseLockControl, Stat, Section, FileUpload } from "../App";

function Phase3({ session, refresh }) {
  const [busy, setBusy] = useState(false);
  const [otaType, setOtaType] = useState("GoMMT");
  const [discovery, setDiscovery] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [localResult, setLocalResult] = useState(null); // Cache POST response directly
  const [localResultOta, setLocalResultOta] = useState(null); // Which OTA the localResult is for
  // Use localResult as immediate display after a run; fall back to session data
  const result = (localResult && localResultOta === otaType)
    ? localResult
    : session.phase_results?.phase3?.[otaType];

  const STD_OTAS = ["GoMMT", "Agoda", "BookingCom", "Hostelworld"];
  const stdSources = discovery?.other_sources;
  const customOTAs = (Array.isArray(stdSources) ? stdSources : []).map((o) => o.source);
  const allOtas = [...STD_OTAS, ...customOTAs];

  const discover = async () => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/audit/session/${session.id}/discover-otas`
      );
      setDiscovery(res.data);
      const sources = res.data.other_sources;
      const otaCount = (Array.isArray(sources) ? sources : []).filter((o) => o.is_likely_ota)
        .length || 0;
      toast.success(`Found ${otaCount} additional OTA source(s) in Master`);
    } catch (e) {
      toast.error(`Discover failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const run = async () => {
    setBusy(true);
    setErrorMsg(null);
    setLocalResult(null);
    setLocalResultOta(null);
    try {
      const isCustom = !STD_OTAS.includes(otaType);
      const body = {
        ota_type: otaType,
        threshold_pct: 1.0,
        commission_outlier_pct: 3.0,
      };
      if (isCustom) body.source_filter = [otaType];
      const res = await axios.post(`${API}/audit/session/${session.id}/phase3`, body);
      // Store result immediately from POST response — don't wait for session refresh
      if (res.data && res.data.ok) {
        setLocalResult(res.data);
        setLocalResultOta(otaType);
        toast.success(`Phase 3 (${otaType}) complete`);
      } else if (res.data && res.data.ok === false) {
        const errMsg = res.data.error || "Unknown backend error";
        toast.error(`Phase 3 failed: ${errMsg}`);
        setErrorMsg(errMsg);
        // Do not set localResult so the UI won't try to render an empty result
        return;
      }
      refresh();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Phase 3 failed: ${msg}`);
      setErrorMsg(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleOverride = async (phase, identifiers, overrideType = "ignored") => {
    try {
      await axios.post(`${API}/audit/session/${session.id}/override`, {
        phase: `phase3:${otaType}`,
        identifiers: Array.isArray(identifiers) ? identifiers : [identifiers],
        resolved: overrideType === "resolved",
        override_type: overrideType || "ignored"
      });
      toast.success("Marked as correct");
      refresh();
    } catch (e) {
      toast.error("Failed to mark correct");
    }
  };

  const getActiveCount = (rows) => {
    if (!rows) return 0;
    const phaseOverrides = session.overrides?.[`phase3:${otaType}`] || [];
    return rows.filter(r => {
      const id = r.booking_id || r.ota_booking_id || (r.ota_pnrs && r.ota_pnrs[0]);
      if (!id) return true;
      return !phaseOverrides.includes(String(id));
    }).length;
  };

  const activeOutliers = getActiveCount(result?.commission_outliers);
  const activeDiscrepancies = getActiveCount(result?.real_discrepancies);
  const activeMismatches = getActiveCount(result?.status_mismatches);
  const activeMissingInOta = getActiveCount(result?.sf_missing_in_ota);

  return (
    <div className="space-y-4">
      <div className="border rounded-md p-3 bg-muted/30">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium">Discover other OTAs from Master</div>
          <Button
            data-testid="discover-otas-btn"
            size="sm"
            variant="outline"
            onClick={discover}
            disabled={busy}
          >
            Scan Master
          </Button>
        </div>
        {Array.isArray(discovery?.other_sources) && discovery.other_sources.length > 0 ? (
          <div className="text-xs space-y-1">
            {discovery.other_sources.map((o) => (
              <div
                key={o.source}
                className="flex items-center justify-between border-b py-1"
              >
                <div>
                  <span className="font-medium">{o.source}</span>{" "}
                  <span className="text-muted-foreground">
                    · {o.row_count} rows · ₹{o.net_amount_sum?.toLocaleString()}
                  </span>
                  {o.is_likely_ota && (
                    <Badge variant="outline" className="ml-2 text-[10px]">
                      OTA
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : discovery ? (
          <p className="text-xs text-muted-foreground">
            No additional OTAs detected. All sources are standard.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Click "Scan Master" to detect Hostelworld, Cleartrip, Airbnb, Expedia, etc.
          </p>
        )}
      </div>

      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-xs">OTA</Label>
          <Select value={otaType} onValueChange={setOtaType}>
            <SelectTrigger className="w-48" data-testid="phase3-ota-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {allOtas.map((o) => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          data-testid="run-phase3-btn"
          onClick={run}
          disabled={busy || !!(session.locked_phases || {})[`phase3:${otaType}`]}
          title={
            (session.locked_phases || {})[`phase3:${otaType}`]
              ? `Phase 3 ${otaType} is locked. Unlock to re-run.`
              : ""
          }
        >
          {busy ? "Running..." : `Run Phase 3 — ${otaType}`}
        </Button>
        <PhaseLockControl
          session={session}
          phaseKey={`phase3:${otaType}`}
          complete={!!(result && result.ok)}
          refresh={refresh}
        />
      </div>

      {otaType === "Hostelworld" ? (
        <div className="text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
          No OTA file is required for Hostelworld. The reconciliation relies entirely on Stayflexi's Master file data.
        </div>
      ) : (
        <MappingEditor session={session} role={`ota:${otaType}`} onSaved={refresh} />
      )}

      {errorMsg && (
        <div className="text-red-500 font-medium bg-red-50 p-3 rounded border border-red-200 mt-4">
          Error: {errorMsg}
        </div>
      )}

      {result && !result.ok && !errorMsg && (
        <div className="text-red-500 font-medium bg-red-50 p-3 rounded border border-red-200 mt-4">
          Error: {result.error || "An unknown error occurred."}
        </div>
      )}

      {result && result.ok && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Master rows (filtered)" value={result.rows_after_filter} />
            <Stat label="OTA rows total" value={result.ota_rows_total} />
            <Stat label="Matched" value={result.matched_count} />
            {result.hotel_collect_count > 0 && (
              <Stat label="Hotel Collect (Direct Pay)" value={result.hotel_collect_count} />
            )}
            <Stat
              label="Real Discrepancies"
              value={activeDiscrepancies}
              danger={activeDiscrepancies > 0}
            />
            <Stat
              label="Commission Outliers"
              value={activeOutliers}
              danger={activeOutliers > 0}
            />
            <Stat
              label="Status Mismatches"
              value={activeMismatches}
              danger={activeMismatches > 0}
            />
            <Stat
              label="Unpaid by OTA"
              value={activeMissingInOta}
              danger={activeMissingInOta > 0}
            />
            <Stat label="TCS/TDS Systematic" value={result.systematic_tcs_tds_count} />
            <Stat label="Refund-only Cancelled" value={result.refund_only_count} />
            <Stat label="Multi-PNR rows" value={result.multi_pnr_row_count} />
            <Stat label="Unmapped OTA Adj." value={result.unmapped_ota_adjustments_count || 0} />
            <Stat
              label="Cancellation Adj."
              value={result.cancellation_adjustment_count}
            />
            <Stat
              label="Duplicate PNR Conflicts"
              value={result.duplicate_pnr_conflicts_count}
              danger={result.duplicate_pnr_conflicts_count > 0}
            />
          </div>

          <Section title="Commission Summary">
            <pre className="bg-muted p-2 rounded text-xs">
              {JSON.stringify(result.commission_summary, null, 2)}
            </pre>
          </Section>

          <Section
            title={`Commission Outliers (${activeOutliers}) — possible revenue leakage`}
          >
            <PreviewTable session={session} rows={result.commission_outliers} onOverride={handleOverride} phaseKey={`phase3:${otaType}`} />
          </Section>

          <Section
            title={`Real Discrepancies (${activeDiscrepancies}) — needs investigation`}
          >
            <PreviewTable session={session} rows={result.real_discrepancies} onOverride={handleOverride} phaseKey={`phase3:${otaType}`} />
          </Section>

          <Section
            title={`Status Mismatches (${activeMismatches}) — SF vs OTA status differ`}
          >
            <PreviewTable session={session} rows={result.status_mismatches} onOverride={handleOverride} phaseKey={`phase3:${otaType}`} />
          </Section>

          <Section
            title={`Unpaid by OTA (${activeMissingInOta}) — In Stayflexi but missing from OTA payout report`}
          >
            <PreviewTable session={session} rows={result.sf_missing_in_ota} onOverride={handleOverride} phaseKey={`phase3:${otaType}`} />
          </Section>

          <Section
            title={`Multi-PNR Rows (${result.multi_pnr_rows?.length || 0}) — single OTA row → multiple SF bookings`}
          >
            <PreviewTable session={session} rows={result.multi_pnr_rows} onOverride={handleOverride} phaseKey={`phase3:${otaType}`} />
          </Section>

          <Section
            title={`Cancellation Adjustments (${result.cancellation_adjustment_count})`}
          >
            <PreviewTable session={session} rows={result.cancellation_adjustments} />
          </Section>

          <Section
            title={`Refund-only Cancelled (${result.refund_only?.length || 0})`}
          >
            <PreviewTable session={session} rows={result.refund_only} />
          </Section>

          <Section
            title={`Unrecorded OTA Payouts (${result.ota_missing_in_sf_count}) — OTA paid but missing in Stayflexi`}
          >
            <PreviewTable session={session} rows={result.ota_missing_in_sf} onOverride={handleOverride} phaseKey={`phase3:${otaType}`} />
          </Section>

          <Section
            title={`Unmapped OTA Adjustments (${result.unmapped_ota_adjustments_count || 0}) — OTA remitted amount without PNR`}
          >
            <PreviewTable session={session} rows={result.unmapped_ota_adjustments} />
          </Section>

          <Section
            title={`Duplicate PNR Conflicts (${result.duplicate_pnr_conflicts_count})`}
          >
            <PreviewTable session={session} rows={result.duplicate_pnr_conflicts} />
          </Section>

          <Section
            title={`TCS/TDS Systematic (${result.systematic_tcs_tds?.length || 0}) — informational`}
          >
            <PreviewTable session={session} rows={result.systematic_tcs_tds} />
          </Section>
        </div>
      )}
      {result && !result.ok && (
        <p className="text-destructive text-sm">{result.error}</p>
      )}
    </div>
  );
}


export default Phase3;
